import { FriendlyCaptchaSDK } from '@friendlycaptcha/sdk'
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'

interface FriendlyCaptchaProps {
  sitekey: string
  onVerify: (response: string) => void
  onError?: (error: Error) => void
}

export interface FriendlyCaptchaRef {
  reset: () => void
}

// Re-use SDK instance across components as recommended in the documentation
const sdk = new FriendlyCaptchaSDK()

export const FriendlyCaptcha = forwardRef<FriendlyCaptchaRef, FriendlyCaptchaProps>(
  ({ sitekey, onVerify, onError }, ref) => {
    const elementRef = useRef<HTMLDivElement>(null)
    const widgetRef = useRef<any>(null)

    // Use refs to store the latest callback functions to avoid recreating the widget
    const onVerifyRef = useRef(onVerify)
    const onErrorRef = useRef(onError)

    // Update refs when callbacks change
    useEffect(() => {
      onVerifyRef.current = onVerify
      onErrorRef.current = onError
    })

    // Expose reset method through ref
    useImperativeHandle(ref, () => ({
      reset: () => {
        if (widgetRef.current?.reset) {
          widgetRef.current.reset()
        }
      },
    }))

    useEffect(() => {
      if (!elementRef.current || widgetRef.current) return

      try {
        const widget = sdk.createWidget({
          element: elementRef.current,
          sitekey: sitekey,
        })

        widgetRef.current = widget

        // Listen for events using refs to always get the latest callbacks
        widget.addEventListener('frc:widget.complete', (event: any) => {
          if (event.detail?.response) {
            onVerifyRef.current(event.detail.response)
          }
        })

        widget.addEventListener('frc:widget.error', (event: any) => {
          onErrorRef.current?.(new Error(event.detail.error?.detail || 'Captcha error'))
        })

        widget.addEventListener('frc:widget.expire', (_event: any) => {
          onErrorRef.current?.(new Error('Captcha expired. Please try again.'))
        })
      } catch (_error) {
        onErrorRef.current?.(new Error('Failed to load captcha'))
      }

      return () => {
        if (widgetRef.current?.destroy) {
          widgetRef.current.destroy()
          widgetRef.current = null
        }
      }
    }, [sitekey])

    return <div ref={elementRef} />
  },
)

FriendlyCaptcha.displayName = 'FriendlyCaptcha'
